/**
 * Pretty-printers for BBEL-templated bodies. `{{ }}` slots aren't valid JSON/XML, so each slot is
 * swapped for a parse-safe sentinel, the document is formatted, and the slots are restored
 * verbatim — a slot's inner expression is never touched.
 */

export type FormatLang = "json" | "xml";

/** Returns the formatted text, or null when the body (slots aside) doesn't parse as `lang`. */
export function formatBbelBody(text: string, lang: FormatLang): string | null {
  if (!text.trim()) return null;
  return lang === "json" ? formatJson(text) : formatXml(text);
}

const SLOT = /\{\{[\s\S]*?\}\}/g;

const token = (i: number) => `__BBEL_SLOT_${i}__`;

// ── JSON ───────────────────────────────────────────────────────────────────────────────────────

/**
 * A slot inside a JSON string becomes a bare token within that string; a slot in value position
 * (`"count": {{ x }}`) must become a *quoted* token to keep the document parseable — restoring
 * strips the quotes again. In-string detection is a linear scan tracking unescaped quotes.
 */
function formatJson(text: string): string | null {
  const slots: { text: string; bare: boolean }[] = [];
  let out = "";
  let inString = false;
  for (let i = 0; i < text.length; i++) {
    if (text.startsWith("{{", i)) {
      const end = text.indexOf("}}", i + 2);
      if (end !== -1) {
        const bare = !inString;
        slots.push({ text: text.slice(i, end + 2), bare });
        out += bare ? `"${token(slots.length - 1)}"` : token(slots.length - 1);
        i = end + 1;
        continue;
      }
    }
    const ch = text[i];
    if (ch === '"' && text[i - 1] !== "\\") inString = !inString;
    out += ch;
  }

  let formatted: string;
  try {
    formatted = JSON.stringify(JSON.parse(out), null, 2);
  } catch {
    return null;
  }

  slots.forEach((slot, i) => {
    formatted = formatted.replace(slot.bare ? `"${token(i)}"` : token(i), slot.text);
  });
  return formatted;
}

// ── XML ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Tag-boundary indenter (no parse): protect regions whose content must stay byte-identical —
 * slots (an expression can contain `<`/`>`), CDATA (can nest markup), comments — break between
 * tags, indent by depth, restore. Mixed content (`<a>text</a>`) has no `><` boundary inside, so
 * text stays glued to its tags.
 */
function formatXml(text: string): string | null {
  const protectedRegions: string[] = [];
  const shield = (s: string) =>
    s.replace(/<!\[CDATA\[[\s\S]*?\]\]>|<!--[\s\S]*?-->|\{\{[\s\S]*?\}\}/g, (m) => {
      protectedRegions.push(m);
      return token(protectedRegions.length - 1);
    });

  const shielded = shield(text.trim());
  // Cheap well-formedness gate: tag angle brackets must balance once protected regions are out.
  const opens = (shielded.match(/</g) ?? []).length;
  const closes = (shielded.match(/>/g) ?? []).length;
  if (opens === 0 || opens !== closes) return null;

  const lines = shielded.replace(/>\s*</g, ">\n<").split("\n");
  let depth = 0;
  const formatted = lines
    .map((line) => {
      const t = line.trim();
      const isClosing = /^<\//.test(t);
      const isSelfContained =
        /^<[^>]*\/>$/.test(t) || /^<\?/.test(t) || /^<!/.test(t) || /^<[^>]+>[\s\S]*<\/[^>]+>$/.test(t);
      if (isClosing) depth = Math.max(0, depth - 1);
      const indented = "  ".repeat(depth) + t;
      if (!isClosing && !isSelfContained && /^</.test(t)) depth++;
      return indented;
    })
    .join("\n");

  return protectedRegions.reduce((acc, region, i) => acc.replace(token(i), region), formatted);
}
